import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2 } from 'lucide-react';

import { env } from '@/env';
import { qcpAuthHeaders } from '@/lib/qcp/auth';
import type { Seat } from '@/lib/qcp/protocol';

interface CpAcceptInviteSheetProps {
  /**
   * Called with the match + the ACCEPTOR's seat once accept succeeds.
   * The acceptor is always seat B (the creator is A).
   */
  onAccepted: (result: { matchId: string; seat: Seat }) => void;
  onClose: () => void;
}

interface InvitePreview {
  creator: { displayName: string; elo: number };
  expiresAt: string;
}

/**
 * Normalise a raw code input to the invite alphabet (A-Z, 2-9), max 10 chars.
 * Strips O/0/1/I which are excluded from the server's alphabet to avoid
 * visual ambiguity (same policy as `normaliseCode` in AcceptInviteSheet).
 */
function normaliseCode(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z2-9]/g, '')
    .slice(0, 10);
}

/**
 * Accept-by-code sheet for Chinese Poker (OFC).
 *
 * Flow: paste 10-char code → GET {qcpInviteUrl}/invites/:code preview
 * (shows creator displayName + elo) → Accept → POST /invites/:code/accept
 * → navigate to `/chinesepoker/match/:matchId` with seat B context.
 *
 * Error mapping:
 *   404              → "expired or already used"
 *   400 cannot_accept_own → dedicated "you can't challenge yourself" message
 *   409              → generic failed (server may refuse double-accept)
 *   any other        → generic failed
 */
export function CpAcceptInviteSheet({ onAccepted, onClose }: CpAcceptInviteSheetProps) {
  const { t } = useTranslation('chinesepoker');

  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<'expired' | 'own' | 'failed' | null>(null);

  async function lookup() {
    if (code.length !== 10) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const res = await fetch(`${env.qcpInviteUrl}/invites/${encodeURIComponent(code)}`, {
        headers: await qcpAuthHeaders(),
      });
      if (res.status === 404) {
        setError('expired');
        return;
      }
      if (!res.ok) throw new Error(`preview failed: ${res.status}`);
      setPreview((await res.json()) as InvitePreview);
    } catch {
      setError('failed');
    } finally {
      setLoading(false);
    }
  }

  async function accept() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${env.qcpInviteUrl}/invites/${encodeURIComponent(code)}/accept`, {
        method: 'POST',
        headers: await qcpAuthHeaders(true),
        body: JSON.stringify({}),
      });
      if (res.status === 400) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error === 'cannot_accept_own' ? 'own' : 'failed');
        return;
      }
      if (res.status === 404) {
        setError('expired');
        return;
      }
      if (res.status === 409) {
        setError('failed');
        return;
      }
      if (!res.ok) throw new Error(`accept failed: ${res.status}`);
      const body = (await res.json()) as { matchId: string; seat: Seat };
      onAccepted({ matchId: body.matchId, seat: body.seat });
    } catch {
      setError('failed');
    } finally {
      setLoading(false);
    }
  }

  const errorText =
    error === 'expired'
      ? t('invite.expired')
      : error === 'own'
        ? t('invite.own')
        : error
          ? t('invite.acceptError')
          : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={t('invite.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <KeyRound className="h-4 w-4 text-brand" />
            {t('invite.haveCode')}
          </h2>
        </div>

        <div className="flex flex-col gap-4 py-2">
          <input
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            value={code}
            onChange={(e) => {
              setCode(normaliseCode(e.target.value));
              setPreview(null);
              setError(null);
            }}
            placeholder="XXXXXXXXXX"
            maxLength={10}
            className="w-full rounded-xl border border-border bg-bg px-4 py-3 text-center text-xl font-bold tracking-[0.25em] text-fg placeholder:text-fg-muted placeholder:tracking-[0.25em]"
          />

          {errorText && <p className="text-sm text-danger">{errorText}</p>}

          {preview && (
            <p className="text-center text-sm text-fg">
              {t('invite.preview', {
                name: preview.creator.displayName,
                elo: preview.creator.elo,
              })}
            </p>
          )}

          {preview ? (
            <button
              type="button"
              onClick={accept}
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('invite.accept')}
            </button>
          ) : (
            <button
              type="button"
              onClick={lookup}
              disabled={code.length !== 10 || loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('invite.lookup')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
